import {
  Container,
  Tabs,
  Tab,
  Box,
  Stack,
  IconButton,
  Typography,
  Chip,
  Alert,
  Button,
  Card,
  CardContent,
  Grid,
  Tooltip,
  Snackbar,
  CircularProgress,
  Paper,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import SettingsIcon from "@mui/icons-material/Settings";
import StorefrontIcon from "@mui/icons-material/Storefront";

import { useRetailStore } from "./hooks/useRetailStore";
import { useAuthStore } from "../../store/authStore";
import POSVenta from "./POSVenta";
import Inventario from "./Inventario";
import Compras from "./Compras";
import Reportes from "./Reportes";
import HistorialVentas from "./HistorialVentas";
import ClientesCRM from "./ClientesCRM";
import PromocionesPrecio from "./PromocionesPrecio";
import GrowthCenter from "./GrowthCenter";
import SecurityAdminPanel from "./SecurityAdminPanel";
import ConfigTienda from "./ConfigTienda";
import FeedbackWA from "../FeedbackWA";
import SubscriptionStatusPanel from "../SubscriptionStatusPanel";
import { useSubscriptionStatus } from "../useSubscriptionStatus";
import { getRetailPermissions, resolveRetailAccess } from "./permissions";
import { resolveRetailStoreName } from "./utils/storeDisplayName";

function StoreSelectionScreen({ loading, stores, onRefresh, onSelect }) {
  const defaultStore = stores.length > 0 ? stores[0] : null;

  return (
    <Container maxWidth="md" sx={{ mt: 4, pb: 6 }}>
      <Typography variant="h5" fontWeight={800} mb={1}>
        Selecciona una tienda
      </Typography>
      <Typography color="text.secondary" mb={3}>
        Elige tu tienda para entrar al POS.
      </Typography>

      {loading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Cargando tiendas...
        </Alert>
      )}

      {!loading && stores.length === 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" fontWeight={700} mb={1}>
              No tienes tiendas asignadas
            </Typography>
            <Typography color="text.secondary" mb={2}>
              Tu sesión está autenticada, pero no existe una relación activa en user_store_roles para ninguna tienda.
            </Typography>
            <Button variant="contained" onClick={onRefresh}>
              Ir a tiendas donde eres vendedor
            </Button>
          </CardContent>
        </Card>
      )}

      {!loading && stores.length > 0 && (
        <>
          <Alert severity="success" sx={{ mb: 2 }}>
            Tienes acceso a {stores.length} tienda{stores.length === 1 ? "" : "s"}. Puedes entrar con un clic.
          </Alert>

          {defaultStore && (
            <Stack direction="row" justifyContent="flex-end" mb={2}>
              <Button size="large" variant="contained" onClick={() => onSelect(defaultStore)}>
                Entrar ahora
              </Button>
            </Stack>
          )}

          <Grid container spacing={2}>
          {stores.map((s) => (
            <Grid item xs={12} key={s.store_id}>
              <Card variant="outlined">
                <CardContent>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", sm: "center" }}
                    spacing={1.5}
                  >
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        {s.store_name}
                      </Typography>
                      <Chip size="small" label={`Rol: ${s.role}`} color="primary" variant="outlined" />
                    </Box>
                    <Button variant="contained" onClick={() => onSelect(s)}>
                      Entrar
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
          </Grid>
        </>
      )}
    </Container>
  );
}

function RetailTenantApp({ activeStore }) {
  const [tab, setTab] = useState(0);
  const [openConfig, setOpenConfig] = useState(false);
  const initializedSecurityEmailRef = useRef("");
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const clearActiveStore = useAuthStore((s) => s.clearActiveStore);
  const store = useRetailStore();
  const { status: subscriptionStatus, daysLeft, expiresAtLabel } = useSubscriptionStatus("retail_pos_v1");
  const isStoreOwner = String(activeStore?.role || "").toUpperCase() === "ADMIN";

  const authEmail = useMemo(
    () => String(user?.email || user?.username || user?.correo || user?.mail || "").trim().toLowerCase(),
    [user]
  );

  useEffect(() => {
    if (!isAuthenticated || !authEmail) return;
    if (initializedSecurityEmailRef.current === authEmail) return;

    const policy = store.securityPolicy;
    const alreadyRegistered =
      policy?.ownerEmail === authEmail ||
      (Array.isArray(policy?.allowedEmails) && policy.allowedEmails.includes(authEmail));
    if (alreadyRegistered) {
      initializedSecurityEmailRef.current = authEmail;
      return;
    }

    initializedSecurityEmailRef.current = authEmail;
    store.inicializarSeguridadUsuario({ email: authEmail }).catch(() => {
      // Keep idempotent behavior and avoid request storms on repeated render cycles.
    });
  }, [authEmail, isAuthenticated, store.inicializarSeguridadUsuario, store.securityPolicy]);

  const access = useMemo(
    () =>
      resolveRetailAccess({
        user,
        isAuthenticated,
        securityPolicy: store.securityPolicy,
        storeRole: activeStore?.role,
      }),
    [activeStore?.role, isAuthenticated, store.securityPolicy, user]
  );

  const role = access.role;
  const permissions = useMemo(() => getRetailPermissions(role), [role]);
  const resolvedStoreName = useMemo(
    () => resolveRetailStoreName(store?.config, activeStore),
    [activeStore, store?.config]
  );
  const flushErrorReason = useMemo(() => {
    const err = store?.flushError?.error;
    const reason =
      err?.message ||
      err?.response?.data?.message ||
      err?.reason ||
      "";
    return String(reason || "").trim();
  }, [store?.flushError?.error]);

  const tabsConfig = useMemo(
    () => [
      {
        key: "POS",
        label: "POS",
        component: <POSVenta store={store} permissions={permissions} role={role} />,
      },
      {
        key: "INVENTARIO",
        label: "Inventario",
        component: <Inventario store={store} permissions={permissions} role={role} />,
      },
      {
        key: "COMPRAS",
        label: "Compras",
        component: <Compras store={store} permissions={permissions} role={role} />,
      },
      {
        key: "VENTAS",
        label: "Ventas",
        component: <HistorialVentas store={store} permissions={permissions} role={role} />,
      },
      {
        key: "CLIENTES",
        label: "Clientes",
        component: <ClientesCRM store={store} permissions={permissions} role={role} />,
      },
      {
        key: "PROMOS",
        label: "Promos",
        component: <PromocionesPrecio store={store} permissions={permissions} role={role} />,
      },
      {
        key: "GROWTH",
        label: "Growth",
        component: <GrowthCenter store={store} permissions={permissions} role={role} />,
      },
      {
        key: "REPORTES",
        label: "Reportes",
        component: <Reportes store={store} permissions={permissions} role={role} />,
      },
      {
        key: "SEGURIDAD",
        label: "Seguridad",
        component: <SecurityAdminPanel store={store} permissions={permissions} role={role} access={access} />,
      },
    ],
    [access, permissions, role, store]
  );

  const tabsVisibles = useMemo(
    () => tabsConfig.filter((t) => permissions.tabs[t.key]),
    [permissions, tabsConfig]
  );

  useEffect(() => {
    if (tab > tabsVisibles.length - 1) {
      setTab(0);
    }
  }, [tab, tabsVisibles.length]);

  const tabActual = tabsVisibles[tab];

  if (store.syncStatus === "subscription_expired") {
    return (
      <SubscriptionStatusPanel
        status="STORE_EXPIRED"
        appName="POS Retail Ropa"
        appKey="retail_pos_v1"
        message="La suscripción de esta tienda ha vencido. Contacta al administrador de la tienda para renovar el acceso."
      />
    );
  }

  if (!access.allowed) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {access.reason || "Acceso denegado al POS"}
        </Alert>
        <Typography color="text.secondary">
          Solo usuarios autenticados y autorizados por el propietario pueden ingresar a la tienda.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 2, pb: 6 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <StorefrontIcon color="primary" />
          <Typography variant="h6" fontWeight={700}>
            {resolvedStoreName}
          </Typography>
          <Chip size="small" label={`Rol: ${role}`} variant="outlined" color="primary" />
          {isStoreOwner && expiresAtLabel && subscriptionStatus !== "NO_ACCESS" && (
            <Chip
              size="small"
              variant="outlined"
              color={subscriptionStatus === "EXPIRING" ? "warning" : "default"}
              label={
                subscriptionStatus === "EXPIRING"
                  ? `Vence en ${daysLeft} día${daysLeft === 1 ? "" : "s"} · ${expiresAtLabel}`
                  : `Suscripción vence el ${expiresAtLabel}`
              }
            />
          )}
          <Chip size="small" label={`Tienda: ${resolvedStoreName}`} variant="outlined" />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" onClick={clearActiveStore}>
            Cambiar tienda
          </Button>
          {permissions.actions.ABRIR_CONFIG && (
            <IconButton onClick={() => setOpenConfig(true)} title="Configuración">
              <SettingsIcon />
            </IconButton>
          )}
        </Stack>
      </Stack>

      {store.syncStatus === "offline" && (
        <Alert
          severity="warning"
          sx={{ mb: 1 }}
          action={
            store.pendingCount > 0 ? (
              <Tooltip title="Reintentar envío de transacciones pendientes">
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => store.flushPendingTransactions?.()}
                >
                  Reintentar ({store.pendingCount})
                </Button>
              </Tooltip>
            ) : undefined
          }
        >
          Sin conexión — trabajando en modo offline.
          {store.pendingCount > 0
            ? ` ${store.pendingCount} transacción${store.pendingCount === 1 ? "" : "es"} en cola.`
            : ""}
        </Alert>
      )}

      {store.syncStatus === "online" && store.pendingCount > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 1 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => store.flushPendingTransactions?.()}
            >
              Enviar ahora ({store.pendingCount})
            </Button>
          }
        >
          Conexión restaurada — {store.pendingCount} transacción{store.pendingCount === 1 ? "" : "es"} pendiente{store.pendingCount === 1 ? "" : "s"} de sincronizar.
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
      >
        {tabsVisibles.map((t) => (
          <Tab key={t.key} label={t.label} />
        ))}
      </Tabs>

      <Box>{tabActual?.component}</Box>

      <ConfigTienda
        store={store}
        activeStore={activeStore}
        resolvedStoreName={resolvedStoreName}
        open={openConfig}
        onClose={() => setOpenConfig(false)}
      />
      <FeedbackWA appName="POS Retail Ropa" />

      {/* Subtle modal while the queue is being sent to the backend */}
      <Snackbar
        open={store.flushingStatus === "flushing"}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Paper
          elevation={4}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2.5,
            py: 1.5,
            borderRadius: 2,
            minWidth: 260,
          }}
        >
          <CircularProgress size={18} thickness={5} />
          <Typography variant="body2" fontWeight={600}>
            Sincronizando transacciones pendientes…
          </Typography>
        </Paper>
      </Snackbar>

      {/* Alert when the queue stalls because a transaction failed */}
      <Snackbar
        open={store.flushingStatus === "failed"}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={store.dismissFlushError}
          action={
            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  store.dismissFlushError();
                  store.flushPendingTransactions?.();
                }}
              >
                Reintentar
              </Button>
              <Tooltip title="Elimina esta transacción de la cola y continúa con las siguientes. Úsalo si el error es permanente (ej. producto duplicado).">
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    store.discardFailedTransaction?.();
                    store.flushPendingTransactions?.();
                  }}
                  sx={{ opacity: 0.8 }}
                >
                  Descartar y continuar
                </Button>
              </Tooltip>
              <Button color="inherit" size="small" onClick={store.dismissFlushError}>
                Cerrar
              </Button>
            </Stack>
          }
          sx={{ maxWidth: 480 }}
        >
          <Typography variant="body2" fontWeight={700} mb={0.25}>
            Cola detenida — no se pudo enviar una transacción
          </Typography>
          {store.flushError?.action && (
            <Typography variant="caption">
              Acción:{" "}
              <strong>{store.flushError.action}</strong>
              {store.flushError?.transactionId
                ? ` · ID: ${String(store.flushError.transactionId).slice(-8)}`
                : ""}
            </Typography>
          )}
          {flushErrorReason && (
            <Typography variant="caption" display="block" mt={0.25}>
              Motivo: {flushErrorReason}
            </Typography>
          )}
          <Typography variant="caption" display="block" mt={0.25}>
            Las transacciones siguientes quedan en espera hasta que esta sea resuelta.
          </Typography>
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default function RetailPOSCore() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const myStores = useAuthStore((s) => s.myStores);
  const activeStoreId = useAuthStore((s) => s.activeStoreId);
  const fetchMyStores = useAuthStore((s) => s.fetchMyStores);
  const selectActiveStore = useAuthStore((s) => s.selectActiveStore);

  const [loadingStores, setLoadingStores] = useState(false);
  const autoSelectedStoreRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    let mounted = true;
    setLoadingStores(true);
    fetchMyStores().finally(() => {
      if (mounted) setLoadingStores(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetchMyStores, isAuthenticated]);

  const activeStore = useMemo(
    () => (myStores || []).find((s) => s.store_id === activeStoreId) || null,
    [activeStoreId, myStores]
  );

  useEffect(() => {
    if (!isAuthenticated || loadingStores || activeStoreId) return;
    if (autoSelectedStoreRef.current) return;
    if (!Array.isArray(myStores) || myStores.length !== 1) return;

    autoSelectedStoreRef.current = true;
    selectActiveStore(myStores[0]);
  }, [activeStoreId, isAuthenticated, loadingStores, myStores, selectActiveStore]);

  if (!activeStoreId || !activeStore) {
    return (
      <StoreSelectionScreen
        loading={loadingStores}
        stores={myStores || []}
        onRefresh={async () => {
          setLoadingStores(true);
          await fetchMyStores();
          setLoadingStores(false);
        }}
        onSelect={selectActiveStore}
      />
    );
  }

  return <RetailTenantApp activeStore={activeStore} />;
}
